import { ASSETS_URL } from "@next-platform/client-common-core/infra/constants";

import { AvatarPreset } from "@/types";

export interface IAvatarUrlOptions {
  preset?: AvatarPreset;
  width?: number;
  height?: number;
  crop?: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- ignore naming convention for now because its legacy code
  crop_gravity?: string;
  focus?: "top" | "center";
  returnWithOriginalRatio?: boolean;
  [key: string]: string | number | boolean | undefined;
}

//We upscale preset for device with higher resolution
export const resolveDevicePixelRatioPreset = (preset: AvatarPreset): AvatarPreset => {
  const devicePixelRatio = window.devicePixelRatio || 1;
  if (devicePixelRatio <= 1) return preset;

  switch (preset) {
    case AvatarPreset.AVATAR_48X48:
      return devicePixelRatio > 2 ? AvatarPreset.AVATAR_256X256 : AvatarPreset.AVATAR_128X128;
    case AvatarPreset.AVATAR_64X64:
      return devicePixelRatio > 2 ? AvatarPreset.AVATAR_256X256 : AvatarPreset.AVATAR_128X128;
    case AvatarPreset.AVATAR_128X128:
      return devicePixelRatio > 2 ? AvatarPreset.AVATAR_400X400 : AvatarPreset.AVATAR_256X256;
    case AvatarPreset.AVATAR_256X256:
      return devicePixelRatio > 2 ? AvatarPreset.AVATAR_640X640 : AvatarPreset.AVATAR_400X400;
    case AvatarPreset.AVATAR_400X400:
      return AvatarPreset.AVATAR_640X640;
    case AvatarPreset.IMAGE_500X650:
      return devicePixelRatio > 2 ? AvatarPreset.IMAGE_960X1248 : AvatarPreset.IMAGE_640X832;
    default:
      return preset;
  }
};

export const getAvatarUrl = (
  key: string,
  // eslint-disable-next-line @typescript-eslint/naming-convention -- ignore naming convention for now because its legacy code
  { preset, width, height, crop, crop_gravity, focus, returnWithOriginalRatio, ...other }: IAvatarUrlOptions = {},
) => {
  if (!key) return null;
  if (key.startsWith("https://")) return key;
  // A local base64 preview (e.g. an attached image restored after a failed send)
  // is already renderable; never prefix it with the CDN host.
  if (key.startsWith("data:")) return key;
  if (preset) {
    preset = resolveDevicePixelRatioPreset(preset);
  }

  const params = [];

  if (preset) {
    params.push(`class=${preset}`);
  }

  if (width) params.push(`width=${width}`);
  if (height) params.push(`height=${height}`);
  if (crop_gravity) params.push(`crop_gravity=${crop_gravity}`);
  if (width && height && !crop) params.push(`aspect_ratio=${width}:${height}`);

  //handle imagekit legacy parameters
  if (focus) {
    switch (focus) {
      case "top":
        params.push("crop_gravity=north");
        break;
      case "center":
        params.push("crop_gravity=center");
        break;
    }
  }

  if (other)
    Object.keys(other).forEach((key) => {
      params.push(`${key}=${other[key]}`);
    });

  if (params.length > 0) {
    //handle if ? is already present in the key
    if (key.includes("?")) {
      return `${ASSETS_URL}/${key}&${params.join("&")}`;
    }
    return `${ASSETS_URL}/${key}?${params.join("&")}`;
  }

  if (returnWithOriginalRatio) {
    return `${ASSETS_URL}/${key}`;
  }
};
